import React from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper,
  Breadcrumbs,
  Link,
  Grid,
  Button,
  Card,
  CardContent
} from '@mui/material';
import { 
  Email as EmailIcon,
  Telegram as TelegramIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const ContactPage: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 4 }}>
        <Link component={RouterLink} to="/" color="inherit">
          Home
        </Link>
        <Typography color="text.primary">Contact Us</Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Contact Us
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
          Have questions, feedback, or need assistance? We're here to help! SharkPanzer is a product of UnwiringTech®. Unwiring Tech GSTIN- 09CYNPA1063Q1ZZ.
        </Typography>
      </Box>
      
      {/* Contact Information Cards */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} md={6}>
          <Card 
            elevation={0} 
            className="glass-container"
            sx={{ 
              height: '100%',
              borderRadius: 4,
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <EmailIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Email Us
              </Typography>
              <Typography variant="body1" paragraph>
                For all inquiries:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                support@unwiringtech.com
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                We strive to respond to all emails within 24-48 hours.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card 
            elevation={0} 
            className="glass-container"
            sx={{ 
              height: '100%',
              borderRadius: 4,
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <TelegramIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Telegram
              </Typography>
              <Typography variant="body1" paragraph>
                Connect with us on Telegram:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                @UNWIRINGLIFE
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                For quick responses and updates.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Live Chat Banner */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4,
          mb: 6,
          borderRadius: 4,
          background: 'linear-gradient(135deg, rgba(0, 180, 216, 0.2) 0%, rgba(0, 150, 199, 0.2) 100%)',
          border: '1px solid',
          borderColor: 'primary.main'
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ChatIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
              <Box>
                <Typography variant="h5" gutterBottom>
                  Need Immediate Assistance?
                </Typography>
                <Typography variant="body1">
                  Our support team is available for live chat 24/7, Monday to Sunday. Get instant help with any questions or issues you may have.
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<ChatIcon />}
              sx={{ 
                borderRadius: 4,
                px: 3,
                py: 1.5
              }}
            >
              Start Live Chat
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Support Page Link */}
      <Paper 
        elevation={0} 
        className="glass-container"
        sx={{ 
          p: 4,
          borderRadius: 4,
          textAlign: 'center'
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom>
          Need More Help?
        </Typography>
        <Typography variant="body1" paragraph sx={{ maxWidth: 700, mx: 'auto' }}>
          Visit our comprehensive support center for tutorials, guides, and frequently asked questions.
        </Typography>
        
        <Button
          variant="contained"
          color="primary"
          component={RouterLink}
          to="/support"
          sx={{ 
            mt: 2,
            borderRadius: 4,
            px: 4,
            py: 1.5
          }}
        >
          Visit Support Center
        </Button>
      </Paper>
    </Container>
  );
};

export default ContactPage;
